import { ThemeEvents } from '@theme/events';

/**
 * Bundle Cart Handler - Prevents individual bundle item removal.
 * When a bundle parent item is removed, all items sharing the same
 * _bundle_id are removed together.
 */

class BundleCartHandler {
  constructor() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate.bind(this));
    this.#init();
  }

  async #init() {
    // On page load, check if cart has bundle items and apply protection
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.#protectBundleItems(cart);
    } catch (err) {
      // Silent fail on init
    }
  }

  /**
   * Handle cart update events.
   * @param {Event} event
   */
  async #onCartUpdate(event) {
    // Don't intercept our own updates
    if (event.detail?.data?.source === 'bundle-cart-handler') return;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.#protectBundleItems(cart);
    } catch (err) {
      console.error('Bundle cart handler error:', err);
    }
  }

  /**
   * Apply bundle protection logic to cart items.
   * @param {Object} cart
   */
  #protectBundleItems(cart) {
    if (!cart?.items?.length) return;

    // Group bundle items by bundle_id
    const bundles = new Map();

    cart.items.forEach((item, index) => {
      const bundleId = item.properties?._bundle_id;
      if (!bundleId) return;

      if (!bundles.has(bundleId)) {
        bundles.set(bundleId, []);
      }
      bundles.get(bundleId).push({ item, index: index + 1 }); // 1-indexed line
    });

    // Hide individual remove buttons for bundle items via CSS
    this.#injectBundleStyles(bundles);
  }

  /**
   * Inject CSS to hide remove buttons for bundle items.
   * @param {Map} bundles
   */
  #injectBundleStyles(bundles) {
    let existingStyle = document.getElementById('bundle-cart-protection');
    if (existingStyle) existingStyle.remove();

    if (bundles.size === 0) return;

    const style = document.createElement('style');
    style.id = 'bundle-cart-protection';

    let css = '';
    bundles.forEach((items) => {
      if (items.length <= 1) return;

      // For child items (those with _parent_id), hide the remove button
      items.forEach(({ item, index }) => {
        if (item.properties?._parent_id) {
          css += `cart-items-component [data-line="${index}"] .cart-item__remove,`;
          css += `cart-items-component [data-line="${index}"] quantity-selector-component {display:none !important;}`;
        }
      });
    });

    if (css) {
      style.textContent = css;
      document.head.appendChild(style);
    }
  }
}

// Initialize on load
if (typeof window !== 'undefined') {
  new BundleCartHandler();
}
